import { valid, parse } from './bi-parsers';


export const ErrorSeverity = {
  RECOVERABLE: 10,
  WARNING: 20,
  ERROR: 30,
  FATAL: 40,
};

export const Categories = {
  EDITOR: 1,
  VIEWER: 2,
  TIMEOUTS: 3,
  SERVER: 4,
};

let disabled = false;

export function disableBiSend() {
  disabled = true;
}

export function now() {
  return new Date().getTime();
}
const startTime = now();

const defaultErrorArgs = {
  _: now,
  ts() {
    return now() - startTime;
  },
  cat: Categories.VIEWER,
  sev: ErrorSeverity.WARNING,
  iss: 1,
  ver: '1',
};

const _initOptions = {
  // The BI server's hostname
  hostName: 'frog.wix.com',

  // The default event args that will be sent with each BI log call (src, time?)
  defaultEventArgs: {},

  // The default event args that will be sent with each error log (src, cat, iss, ver)
  defaultErrorArgs: {},

  // The BI server URL
  biUrl: '//frog.wix.com/',

  // Schemas validators, should look like this:
  // {match: () => true, execute: () => undefined}
  schemaValidators: [],

  // The BI adapter (maps to a path in the url)
  adapter: '',

  // error output stream
  error(str) {
    console.warn(str); // eslint-disable-line no-console
  },

  sendInvalidEvents: true,
};

const EVENT_IDS = {
  ERROR: 10,
  ON_READY: 302,
  ROUTE_CHANGE: 300,
};

function _extend(dst) {
  for (let i = 1; i < arguments.length; i += 1) {
    const extending = arguments[i];
    if (extending) {
      for (const key in extending) {
        if (extending.hasOwnProperty(key)) {
          dst[key] = extending[key];
        }
      }
    }
  }
  return dst;
}


const _options = _extend({}, _initOptions);

export function event(eventArgs, callback = _.noop, defaultEventArgs) {
  const _biFieldsRestrictions = {
    src: { type: 'number' },
    evid: { type: 'number' },
  };

  const eventParams = _extend({}, defaultEventArgs, _options.defaultEventArgs, eventArgs);

  const schemaValidators = _options.schemaValidators || [];
  const validatorsResult = schemaValidators.length > 0 ?
    _.some(schemaValidators, function (validator) {
      return validator.match(eventParams) && (validator.execute(eventParams) || true);
    }) : true;

  if (!validatorsResult) {
    throw new Error('No validator accepted the event');
  }
  return new Promise((resolve, reject) => {
    if (!validateBiEventArgs(eventParams, ['evid'], _biFieldsRestrictions) && !_options.sendInvalidEvents) {
      reject('Event is invalid');
      return;
    }
    sendBiEvent(eventParams, (...args) => {
      resolve();
      callback(...args);
    });
  });

}

export function error(errorArgs, callback = _.noop) {
  const _requiredErrorFields = ['evid', 'cat', 'iss', 'sev', 'errc', 'ver'];
  const _errorFieldsRestrictions = {
    src: { type: 'number' },
    evid: { type: 'number', values: [EVENT_IDS.ERROR] },
    cat: {
      type: 'number',
      values: [Categories.EDITOR, Categories.VIEWER, Categories.TIMEOUTS, Categories.SERVER],
    },
    iss: { type: 'number' },
    sev: {
      type: 'number',
      values: [ErrorSeverity.RECOVERABLE, ErrorSeverity.WARNING, ErrorSeverity.ERROR, ErrorSeverity.FATAL],
    },
    errc: { type: 'number' },
    httpc: { type: 'number' },
    ver: { type: 'string', maxLength: 16 },
    errscp: { type: 'string', subStr: 64 },
    trgt: { type: 'string', subStr: 64 },
    gsi: { type: 'string', length: 36 },
    ts: { type: 'number' },
    uid: { type: 'number' },
    ut: { type: 'string', maxLength: 16 },
    did: { type: 'string', maxLength: 36 },
    cid: { type: 'string', length: 36 },
    lng: { type: 'string', maxLength: 5 },
    dsc: { type: 'string', subStr: 512 },
  };

  const errorParams = _extend({}, defaultErrorArgs, _options.defaultErrorArgs, errorArgs, { evid: EVENT_IDS.ERROR });

  return new Promise((resolve, reject) => {
    if (!validateBiEventArgs(errorParams, _requiredErrorFields,
        _errorFieldsRestrictions) && !_options.sendInvalidEvents) {
      reject('Error event is invalid');
      return;
    }
    sendBiEvent(errorParams, (...args) => {
      resolve();
      callback(...args);
    });
  });
}

export function validateBiEventArgs(eventArgs, requiredArgs, restrictions, id) {
  const missingRequiredArgs = requiredArgs.slice(0);
  for (const key in eventArgs) {
    if (eventArgs.hasOwnProperty(key)) {
      // validate & parse the key
      const currentRestrictions = restrictions[key] || {};

      eventArgs[key] = parse(eventArgs[key], currentRestrictions);
      if (!valid(eventArgs[key], currentRestrictions)) {
        _options.error(
          [
            `\t${id}. Bad event param (key: ${key}, value: ${JSON.stringify(eventArgs[key])})`,
            `\tCurrent restrictions: ${JSON.stringify(restrictions[key])}\n`,
          ].join('\n')
        );
        return;
      }

      // remove the key from the required fields
      const missingIndex = missingRequiredArgs.indexOf(key);
      if (missingIndex > -1) {
        missingRequiredArgs.splice(missingIndex, 1);
      }
    }
  }

  if (missingRequiredArgs.length > 0) {
    _options.error(`Missing required params: ${missingRequiredArgs.join(', ')}`);
    return false;
  }

  return true;
}

function addUrlParams(url, params) {
  const delimiter = url.match(/\?./) ? '&' : '?';
  return url.replace(/\?$/, '') + delimiter +
    _(params)
      .keys()
      .filter(function (key) {
        return !_options.removeUndefinedValues || !_.isUndefined(params[key]);
      })
      .map(function (key) {
        return [encodeURIComponent(key), '=', encodeURIComponent(params[key])].join('');
      })
      .join('&');
}

function sendBiEvent({ adapter = _options.adapter, ...eventArgs }, onComplete = _.noop) {
  if (disabled) {
    onComplete(_options.biUrl);
    return;
  }
  const url = addUrlParams(_options.biUrl + adapter, eventArgs);

  // The magic line
  const biImage = new Image(0, 0);
  biImage.onload = onComplete;
  biImage.onerror = onComplete;
  biImage.src = url;
}



// WEBPACK FOOTER //
// ./src/shared/bi/bi-core.js